import { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from "react";
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { useLocation } from "wouter";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey);

const SESSION_KEY = "bazaar_support_token";

export interface BazaarUser {
  id: string;
  email: string;
  username: string;
  displayName?: string | null;
  avatar?: string | null;
  traderLevel?: string;
  points?: number;
  isAdmin?: boolean;
}

/**
 * What the register form collects. Mirrors the server's registerBodySchema —
 * the first five are required THERE, so they are required here rather than
 * being validated twice with rules that can drift apart.
 *
 * 🔴 dateOfBirth is not optional and must not become optional. It is the source
 * of truth for the server-side minimum-age gate, which fails closed: a missing or
 * malformed value is rejected outright. A form that treats it as a nicety would
 * produce accounts the server refuses, and the person would not know why.
 */
export interface SignUpInput {
  username: string;
  email: string;
  password: string;
  /** YYYY-MM-DD. */
  dateOfBirth: string;
  acceptedTerms: boolean;
  firstName?: string;
  referralCode?: string;
  /** Category slugs picked on step 2. */
  categoryInterests?: string[];
  /**
   * The visitor's OWN answer to "how did you hear about us?" — step 3's chips.
   * Omitted when they skipped it, so the server stores its "unknown" default and
   * the field stays writable later. See the wire body for why this carries the
   * channel and not the surface.
   */
  acquisitionSource?: string;
}

interface AuthContextValue {
  user: BazaarUser | null;
  bazaarToken: string | null;
  loading: boolean;
  isAdmin: boolean;
  /** True when this user's beta access was ended by an admin (forced interstitial). */
  betaEnded: boolean;
  /** Message to show on the beta-ended interstitial. */
  betaEndedMessage: string | null;
  /** Sign in via the Express API (Postgres JWT). Works for both regular users and admins. */
  signIn: (identifier: string, password: string) => Promise<{ error: string | null }>;
  /**
   * Create an account via the Express API, and land signed in.
   *
   * POST /api/auth/register answers 201 with the SAME `{ token, refreshToken,
   * user }` shape login answers, so this stores the session exactly as signIn
   * does. Registering and then asking the visitor to sign in would be a second
   * form for a session we were already handed.
   *
   * The five fields below are the server's own required set (registerBodySchema)
   * and nothing more. firstName, categoryInterests, genderPreference and the
   * referral code are optional there and stay optional here — Nick's ruling
   * 2026-08-22 is that skipping first name "must cost nothing: no validation
   * error, no second screen".
   *
   * 🪤 IT RETURNS THE TOKEN, and the caller needs it. Registration does not end
   * at 201 — the page still has a BLOCKING shipping-address PUT and, optionally,
   * two Stripe calls to make, and `requireAuth` on the server is Bearer-ONLY
   * (middleware/auth.ts resolveUser returns null without an Authorization
   * header; the session cookie buys nothing there). `authFetch` reads the token
   * out of React state, and the closure the submit handler is holding was made
   * on the render that began the submit — when the token was still null. Using
   * it here would send no header, 401 the shipping address, and fail the whole
   * registration for someone whose account was in fact created. So the token
   * comes back by return value, which is true the instant 201 lands.
   *
   * `keepSession` is asked the instant 201 lands; `false` means the person who
   * started this sign-up is no longer the one on screen (the web sign-up sheet
   * was closed or reopened for someone else, card 1219178426987205), so the
   * session is NOT stored: the account exists, this tab is simply not signed in
   * to it. The token is still returned. Absent, the session is stored as before.
   */
  signUp: (
    input: SignUpInput,
    opts?: { keepSession?: () => boolean },
  ) => Promise<{ error: string | null; token: string | null }>;
  signOut: () => Promise<void>;
  /**
   * Sign THIS BROWSER out of `token`, locally only, and only if `token` is still
   * the stored session (a sign-in or another tab since is left alone). Storage,
   * React state and realtime auth are cleared exactly as clearSession does.
   *
   * 🔴 Never the server logout. It is for a web sign-up that stored a session
   * and then never finished (card 1219178426987205, a shared iPad): the next
   * person to open the sign-up sheet must not be left signed in as the last one,
   * and POST /api/auth/logout would revoke the refresh cookie, which by then may
   * be the next person's.
   */
  clearLocalSession: (token: string) => void;
  /**
   * Store a session the server already issued, the way signIn and signUp store
   * theirs. For the website's Google and Apple sign-in (src/web/authSeam.tsx):
   * POST /api/auth/oauth/* answers the same `{ token, user }`, and the seam
   * decides WHEN to keep it (only once the account's sign-up is finished).
   */
  adoptSession: (token: string, user: BazaarUser) => void;
  /** The session token stored in this browser right now (every tab shares it), or null. */
  storedToken: () => string | null;
  /**
   * Authenticated fetch wrapper — handles 401 → refresh → retry transparently.
   * Use this instead of bare `fetch` for API calls that require auth.
   */
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Attempt a cookie-based token refresh. Returns new token string or null. */
async function attemptRefresh(): Promise<string | null> {
  try {
    const res = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({})) as { token?: string };
    return data.token ?? null;
  } catch {
    return null;
  }
}

/**
 * ─── ONE REFRESH AT A TIME ──────────────────────────────────────────────────
 *
 * 🔴 WHY THIS EXISTS. `authFetch` below retries a 401 by refreshing, and until
 * now every caller refreshed privately. One request at a time, that is fine.
 * The Import Hub's folder drop is what makes it not fine: thirty photos upload
 * concurrently, and if the token expires mid-drop then thirty requests 401
 * within the same instant and fire THIRTY `POST /api/auth/refresh` calls at a
 * server that rotates the refresh cookie. Each one is a real round-trip, and if
 * rotation invalidates the cookie the first one consumed, the other twenty-nine
 * get a failed refresh for a session that is in fact perfectly alive.
 *
 * 🔴 AND THE FAILURE IS NOT MERELY WASTEFUL — IT SIGNS THE SELLER OUT. The
 * `!newToken` branch in `authFetch` calls `clearSession()`. So the losers of
 * that race do not just retry badly, they conclude the session is dead and drop
 * the seller onto the login screen in the middle of an upload. NO CALLER MAY
 * CONCLUDE THE SESSION DIED FROM ITS OWN PRIVATE ATTEMPT; every clear-on-failure
 * path has to be driven by the ONE shared result, which is what this gives them.
 *
 * Mirrors the fix mobile already shipped for the same defect (PR #676,
 * `_refreshInFlight` in artifacts/mobile/lib/api.ts). That change was scoped
 * `fix(mobile,api)` and never touched this file, so the web app still had the
 * original bug — the folder-drop path is simply the first thing that exposes it.
 *
 * ⚠️ Nothing is passed in. The web refresh is COOKIE-based (`attemptRefresh`
 * above sends no token and reads none), so unlike mobile there is no captured
 * per-caller token that could already be spent by the time the flight runs.
 * That is why the parameter this function used to take was not merely unused
 * but actively misleading once callers share one flight, and is gone.
 */
let _refreshInFlight: Promise<string | null> | null = null;

/**
 * Rotate the session, coalescing concurrent callers onto one server round-trip.
 * Never rejects — callers branch on null.
 */
function refreshSession(): Promise<string | null> {
  if (_refreshInFlight) return _refreshInFlight;
  const flight = attemptRefresh()
    .catch(() => null)
    // Cleared on BOTH settle paths. A cached result would pin the session to
    // one moment's answer for the life of the tab: a cached rejection makes the
    // session unrecoverable after a single transient blip, and a cached success
    // would keep handing out a token long after it too has expired.
    .finally(() => { _refreshInFlight = null; });
  _refreshInFlight = flight;
  return flight;
}

/**
 * Test seam: drop any in-flight refresh so one test's flight cannot be handed
 * to the next test. Module state outlives a component unmount by design — that
 * is the whole point of the coalescing — so the suite needs a way to reset it.
 */
export function __resetRefreshInFlightForTests(): void {
  _refreshInFlight = null;
}

type AuthChannelMessage = { type: "logout" };

const AUTH_CHANNEL = "bazaar_auth";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [bazaarUser, setBazaarUser] = useState<BazaarUser | null>(null);
  const [bazaarToken, setBazaarToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [betaEnded, setBetaEnded] = useState(false);
  const [betaEndedMessage, setBetaEndedMessage] = useState<string | null>(null);
  const [, navigate] = useLocation();

  /** Inspect a non-OK response for the beta_ended machine code and trip the forced interstitial. */
  const detectBetaEnded = useCallback(async (res: Response): Promise<boolean> => {
    if (res.status !== 403) return false;
    try {
      const body = await res.clone().json().catch(() => ({}));
      if ((body as { code?: string }).code === "beta_ended") {
        setBetaEnded(true);
        setBetaEndedMessage((body as { error?: string }).error ?? null);
        return true;
      }
    } catch { /* ignore parse errors */ }
    return false;
  }, []);

  const channelRef = useRef<BroadcastChannel | null>(null);
  const isAuthenticatedRef = useRef(false);

  const setSession = useCallback((token: string, userData: BazaarUser) => {
    localStorage.setItem(SESSION_KEY, token);
    localStorage.setItem(`${SESSION_KEY}_user`, userData.username);
    setBazaarToken(token);
    setBazaarUser(userData);
    // Recovery: a successful session means beta access is back (admin restore
    // or beta_mode flipped OFF). Clear the sticky interstitial state so the
    // user is no longer trapped on BetaEndedScreen in the same SPA session.
    setBetaEnded(false);
    setBetaEndedMessage(null);
    supabase.realtime.setAuth(token);
  }, []);

  // Clears auth tokens ONLY — deliberately preserves betaEnded state so a
  // revoked user on a persisted session is forced into the no-escape
  // interstitial even though their (now-rejected) token gets cleared.
  const clearSession = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(`${SESSION_KEY}_user`);
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(`${SESSION_KEY}_user`);
    setBazaarToken(null);
    setBazaarUser(null);
    supabase.realtime.setAuth(null);
  }, []);

  const clearLocalSession = useCallback((token: string) => {
    if (localStorage.getItem(SESSION_KEY) === token) clearSession();
  }, [clearSession]);

  const storedToken = useCallback(() => localStorage.getItem(SESSION_KEY), []);

  // Keep isAuthenticatedRef current so the channel handler can check auth state
  // without the effect needing to depend on bazaarToken (which would recreate the channel on every login)
  useEffect(() => {
    isAuthenticatedRef.current = !!bazaarToken;
  }, [bazaarToken]);

  // Open the BroadcastChannel once and listen for logout signals from other tabs
  useEffect(() => {
    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent<AuthChannelMessage>) => {
      if (event.data?.type === "logout" && isAuthenticatedRef.current) {
        clearSession();
        navigate("/login");
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [clearSession, navigate]);

  useEffect(() => {
    let mounted = true;

    async function init() {
      const storedToken = localStorage.getItem(SESSION_KEY);
      if (storedToken) {
        try {
          const res = await fetch("/api/auth/me", {
            headers: { Authorization: `Bearer ${storedToken}` },
            credentials: "include",
          });

          if (res.ok && mounted) {
            const userData = await res.json();
            const u: BazaarUser = {
              id: userData.id,
              email: userData.email,
              username: userData.username,
              displayName: userData.displayName,
              avatar: userData.avatar,
              traderLevel: userData.traderLevel,
              points: userData.points,
              isAdmin: userData.isAdmin === true,
            };
            setBazaarUser(u);
            setBazaarToken(storedToken);
            // Recovery on load: a successful /api/auth/me means beta access is
            // back (admin restore or beta_mode OFF), so never start trapped.
            setBetaEnded(false);
            setBetaEndedMessage(null);
            supabase.realtime.setAuth(storedToken);
          } else if (res.status === 401 && mounted) {
            // Try cookie-based refresh
            const newToken = await refreshSession();
            if (newToken && mounted) {
              const meRes = await fetch("/api/auth/me", {
                headers: { Authorization: `Bearer ${newToken}` },
              });
              if (meRes.ok && mounted) {
                const userData = await meRes.json();
                const u: BazaarUser = {
                  id: userData.id,
                  email: userData.email,
                  username: userData.username,
                  displayName: userData.displayName,
                  avatar: userData.avatar,
                  traderLevel: userData.traderLevel,
                  points: userData.points,
                  isAdmin: userData.isAdmin === true,
                };
                setSession(newToken, u);
              } else if (mounted) {
                clearSession();
              }
            } else if (mounted) {
              clearSession();
            }
          } else if (!res.ok && mounted) {
            // Beta access ended by an admin — trip the forced interstitial before clearing.
            await detectBetaEnded(res);
            clearSession();
          }
        } catch {
          // Network error — keep token for next load
        }
      }

      if (mounted) setLoading(false);
    }

    init();

    return () => {
      mounted = false;
    };
  }, [setSession, clearSession, detectBetaEnded]);

  const isAdmin = bazaarUser?.isAdmin === true;
  const user = bazaarUser;

  /**
   * Authenticated fetch wrapper with 401 → refresh → retry logic.
   * Sends credentials (cookies) automatically for HTTPOnly cookie refresh.
   */
  const authFetch = useCallback(async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const currentToken = bazaarToken;
    const headers: Record<string, string> = {
      ...(init?.headers as Record<string, string> | undefined),
    };
    if (currentToken) {
      headers["Authorization"] = `Bearer ${currentToken}`;
    }

    const res = await fetch(input, { ...init, headers, credentials: "include" });

    if (res.status === 401 && currentToken) {
      // Try cookie-based refresh
      // Shared flight: see refreshSession. Thirty concurrent uploads that all
      // 401 make ONE refresh call, and all thirty read the same answer — so a
      // rotation that succeeded for one of them cannot look like a dead session
      // to the other twenty-nine and sign the seller out mid-drop.
      const newToken = await refreshSession();
      if (newToken) {
        localStorage.setItem(SESSION_KEY, newToken);
        setBazaarToken(newToken);
        supabase.realtime.setAuth(newToken);
        // Retry original request with new token
        const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
        const retryRes = await fetch(input, { ...init, headers: retryHeaders, credentials: "include" });
        // Beta access may have ended — check the retried response too.
        if (retryRes.status === 403) {
          await detectBetaEnded(retryRes);
        }
        return retryRes;
      }
      // Refresh failed — clear session
      clearSession();
    }

    // Beta access ended mid-session — trip the forced interstitial.
    if (res.status === 403) {
      await detectBetaEnded(res);
    }

    return res;
  }, [bazaarToken, clearSession, detectBetaEnded]);

  /**
   * Sign in via the Express API (Postgres + bcrypt).
   * Works for both regular users and admins — isAdmin comes from the users table.
   */
  async function signIn(identifier: string, password: string): Promise<{ error: string | null }> {
    let errorMessage = "Could not reach the server. Please try again.";
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ identifier: identifier.trim(), password }),
      });
      if (res.ok) {
        const data = await res.json();
        const u: BazaarUser = {
          id: data.user.id,
          email: data.user.email,
          username: data.user.username,
          displayName: data.user.displayName,
          avatar: data.user.avatar,
          traderLevel: data.user.traderLevel,
          points: data.user.points,
          isAdmin: data.user.isAdmin === true,
        };
        setSession(data.token, u);
        return { error: null };
      }
      const body = await res.json().catch(() => ({}));
      // Beta access ended by an admin — trip the forced interstitial.
      if (res.status === 403 && (body as { code?: string }).code === "beta_ended") {
        setBetaEnded(true);
        setBetaEndedMessage((body as { error?: string }).error ?? null);
      }
      errorMessage = (body as { error?: string }).error ?? "Invalid username or password.";
    } catch {
      // Network error
    }
    return { error: errorMessage };
  }

  /**
   * Create an account and land signed in. Deliberately parallel to signIn — same
   * fetch posture, same session storage, same error shape — so the two paths
   * cannot drift on what "signed in" means.
   */
  async function signUp(
    input: SignUpInput,
    opts?: { keepSession?: () => boolean },
  ): Promise<{ error: string | null; token: string | null }> {
    let errorMessage = "Could not reach the server. Please try again.";
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          username: input.username.trim(),
          email: input.email.trim(),
          password: input.password,
          dateOfBirth: input.dateOfBirth,
          acceptedTerms: input.acceptedTerms,
          /* Omitted rather than sent empty: the server bounds these when
             present, and "" is not the same as "not given". */
          ...(input.firstName?.trim() ? { firstName: input.firstName.trim() } : {}),
          ...(input.referralCode?.trim() ? { referralCode: input.referralCode.trim() } : {}),
          ...(input.categoryInterests?.length ? { categoryInterests: input.categoryInterests } : {}),
          /**
           * 🔴 THE CHANNEL GOES IN `acquisitionSource`. THE SURFACE GOES IN
           * `acquisitionMedium`. This used to send the literal "website" as the
           * SOURCE, and that was wrong twice over.
           *
           * `acquisitionSource` answers "what's working" — the app writes the
           * visitor's own pick from friend / social / app_store / ad / other, and
           * admin-acquisition.ts groups signups by `source::medium::campaign` to
           * rate channels. "website" is not a channel; it is which of our two
           * front doors they walked through, which is exactly what the `medium`
           * column beside it is for and which sat at "unknown" for every web
           * signup while "website" squatted in the slot next to it.
           *
           * 🪤 And it FROZE the field. `POST /me/registration-details` writes
           * acquisitionSource only while it is still the "unknown" default
           * (users.ts, "first write wins"), and `PATCH /me` refuses it on purpose
           * — attribution a user can rewrite is not attribution. A web signup
           * arrived pre-stamped "website", so its real channel could never be
           * recorded, by that route or any other. Every web account's attribution
           * was destroyed at the moment of signup, and the report counted them as
           * a channel called "website".
           *
           * Nothing read the old value: the only reference was the test that
           * pinned it, and get-redirect.ts's "website" is a store campaign token
           * on a different subsystem that never touches the users table.
           */
          ...(input.acquisitionSource ? { acquisitionSource: input.acquisitionSource } : {}),
          acquisitionMedium: "website",
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const u: BazaarUser = {
          id: data.user.id,
          email: data.user.email,
          username: data.user.username,
          displayName: data.user.displayName,
          avatar: data.user.avatar,
          traderLevel: data.user.traderLevel,
          points: data.user.points,
          /* The register response carries no isAdmin, and a brand-new account is
             never an admin. `false` is the honest read, not a default. */
          isAdmin: false,
        };
        if (opts?.keepSession?.() !== false) setSession(data.token, u);
        return { error: null, token: data.token as string };
      }
      const body = await res.json().catch(() => ({}));
      /* The server's OWN sentence, not ours. It distinguishes "that email is
         taken" (409) from "that username is taken" (409) from the beta waitlist
         refusal (403) and from each field's validation message — all of which
         are more useful than anything we could invent, and all of which a
         generic string would throw away. */
      errorMessage = (body as { error?: string }).error ?? "Could not create your account.";
    } catch {
      // Network error — errorMessage keeps its reachability wording.
    }
    return { error: errorMessage, token: null };
  }

  async function signOut(): Promise<void> {
    const currentToken = bazaarToken;
    // Revoke server-side (deletes refresh token from DB, clears HTTPOnly cookie)
    await fetch("/api/auth/logout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      ...(currentToken ? { body: JSON.stringify({}) } : {}),
    }).catch(() => {});
    clearSession();
    // Explicit user-initiated logout dismisses any beta-ended interstitial.
    setBetaEnded(false);
    setBetaEndedMessage(null);
    // Notify other tabs to log out too (BroadcastChannel does not deliver to sender)
    channelRef.current?.postMessage({ type: "logout" } satisfies AuthChannelMessage);
  }

  return (
    <AuthContext.Provider value={{ user, bazaarToken, loading, isAdmin, betaEnded, betaEndedMessage, signIn, signUp, signOut, clearLocalSession, adoptSession: setSession, storedToken, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
